import { acceptHMRUpdate, defineStore } from 'pinia'
import api from '@api'
import type { BlankEnum, DefaultPriceSettings } from '@apiTypes'
import { VehicleTypeEnum } from '@apiTypes'

/** The settings singleton's SPA-facing switches, as they arrive on the wire. */
export type PriceSettings = DefaultPriceSettings

/** What each special requirement adds to the price, by requirement (a number, or a decimal string). */
export type SpecialRequirementPrices = Partial<Record<string, number | string>>

export const usePriceStore = defineStore('price', {
  state: () => ({
    defaultPrice: undefined as number | undefined,
    specialRequirementPrices: {} as SpecialRequirementPrices,
    priceLoading: true,
    defaultPriceSettings: {} as PriceSettings,
    lastFetchedPriceSettings: 0 as number,
    fetchPriceSettingsPromise: null as Promise<PriceSettings> | null,
    vehicleType: VehicleTypeEnum.VAN as VehicleTypeEnum | BlankEnum | undefined,
  }),
  getters: {
    // Runtime feature switches live on the same singleton as show_price and
    // are served by the same endpoint — there is no second flags call.
    // Defaults to off: an unfetched or failed load must hide a dark feature
    // rather than flash it.
    isLoyaltyEnabled: (state): boolean =>
      state.defaultPriceSettings.loyalty_program_enabled === true,
  },
  actions: {
    /**
     * Resolve the settings without throwing, for callers that must not fail a
     * navigation — the router guards. `getDefaultPriceSettings` rejects on a
     * network error; here that degrades to the current (off) defaults, and
     * because a failed fetch never stamps `lastFetchedPriceSettings`, the next
     * caller retries rather than pinning the session.
     */
    async ensureSettingsLoaded(): Promise<PriceSettings> {
      try {
        return await this.getDefaultPriceSettings()
      }
      catch {
        return this.defaultPriceSettings
      }
    },

    setVehicleType(vehicleType: VehicleTypeEnum | BlankEnum | undefined) {
      this.vehicleType = vehicleType
    },
    setDefaultPrice(price: number) {
      this.defaultPrice = price
    },
    setSpecialRequirementPrices(data: SpecialRequirementPrices) {
      this.specialRequirementPrices = data
    },
    setLoading(loading: boolean) {
      this.priceLoading = loading
    },
    async getDefaultPriceSettings(): Promise<PriceSettings> {
      const currentTime = Date.now()
      const refreshInterval = 300000

      if (this.fetchPriceSettingsPromise)
        return this.fetchPriceSettingsPromise

      const shouldFetch = !this.lastFetchedPriceSettings || (currentTime - this.lastFetchedPriceSettings) > refreshInterval

      if (shouldFetch) {
        this.fetchPriceSettingsPromise = api.getDefaultPriceSettings.defaultPriceSettingsGetDefaultPriceSettingsRetrieve()
          .then(response => {
            this.defaultPriceSettings = response.data
            this.lastFetchedPriceSettings = currentTime

            return this.defaultPriceSettings
          })
          .catch(error => {
            console.error('Failed to fetch default price settings:', error)
            throw error
          })
          .finally(() => {
            this.fetchPriceSettingsPromise = null
          })

        return this.fetchPriceSettingsPromise
      }

      return Promise.resolve(this.defaultPriceSettings)
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(usePriceStore, import.meta.hot))
